/* eslint-disable react-refresh/only-export-components -- a navigation module: one component (Link) plus the hooks that belong with it, mirroring the next-intl API this app was written against. */
import { useCallback, useMemo, type AnchorHTMLAttributes, type Ref } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useLocale } from "use-intl";
import { localizeHref, splitLocalePath, type AppLocale } from "./routing";

/**
 * Locale-aware Link, useRouter and usePathname — use these instead of the
 * plain react-router equivalents for in-app navigation, so every link
 * automatically carries the current `/:locale` prefix. The API mirrors the
 * next-intl navigation helpers the app was originally written against, so
 * components navigate with plain app paths ("/dashboard") and never build
 * locale prefixes themselves.
 */

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  href: string;
  /** Link to the same path in a different interface locale. */
  locale?: AppLocale;
  replace?: boolean;
  ref?: Ref<HTMLAnchorElement>;
};

export function Link({ href, locale, replace, ref, ...rest }: LinkProps) {
  const currentLocale = useLocale() as AppLocale;
  const isInternal = href.startsWith("/") && !href.startsWith("//");
  if (!isInternal) {
    return <a ref={ref} href={href} {...rest} />;
  }
  return <RouterLink ref={ref} to={localizeHref(href, locale ?? currentLocale)} replace={replace} {...rest} />;
}

type NavigateOptions = { locale?: AppLocale; scroll?: boolean };

export function useRouter() {
  const navigate = useNavigate();
  const currentLocale = useLocale() as AppLocale;
  const queryClient = useQueryClient();

  const to = useCallback(
    (href: string, options?: NavigateOptions) => localizeHref(href, options?.locale ?? currentLocale),
    [currentLocale],
  );

  return useMemo(
    () => ({
      push: (href: string, options?: NavigateOptions) => void navigate(to(href, options)),
      replace: (href: string, options?: NavigateOptions) => void navigate(to(href, options), { replace: true }),
      back: () => void navigate(-1),
      /**
       * Re-fetches all server data currently on screen (e.g. after login or
       * logout, so the session and everything derived from it update).
       */
      refresh: () => void queryClient.invalidateQueries(),
    }),
    [navigate, to, queryClient],
  );
}

/** The current path WITHOUT its locale prefix ("/uz/dashboard" → "/dashboard"). */
export function usePathname(): string {
  const { pathname } = useLocation();
  return splitLocalePath(pathname).pathname;
}
